import React, { useCallback, useState, useEffect, useMemo } from 'react'
import PlotlyPlot from 'react-plotly.js'
import styled from 'styled-components'
import * as Plotly from 'plotly.js'

import RthPlot from 'pages/shared/Device/RthPlot'
import HoverBox from 'pages/Device/ThermostatRthPanel/Chart/HoverBox'
import GraphFilterModal from 'pages/Device/ThermostatRthPanel/GraphFilterModal'
import { getDefaultSelectedSeriesItems } from 'pages/Device/ThermostatRthPanel/Chart/plotHelper'

import Spinner from 'components/Spinner'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import { RuntimeHistory } from 'types/RuntimeHistory'

import { NoData, screenSize } from 'styles/shared'
import { color } from 'styles/colors'

import { chartHeight } from 'helpers/plotly/utils'
import GA4 from 'helpers/ga4'

interface ZoneSeries {
  name: string
  series: Array<string> | null
}

export interface Figure {
  data: Plotly.Data[];
  layout: Partial<Plotly.Layout>;
  frames: Plotly.Frame[] | null;
}
interface SelectedLegends {
  series: Array<string> | null
  zoneSeries: Array<ZoneSeries> | null
  alerts: Array<string> | null
}

type Props = {
  deviceId: string
  chartDivId: string
  startTime: string
  endTime: string
  timeZone: string
  isZoomed: boolean
  zoomedCallback: (isZoomed: boolean) => void
  isRTHDataLoaded: boolean
  rthData: RuntimeHistory | null
  updateSelectedLegendsCallback: (selectedLegendItem: SelectedLegends) => void
}

const FullScreenPlot = ({ deviceId, chartDivId, startTime, endTime, timeZone, rthData, isRTHDataLoaded, isZoomed, zoomedCallback, updateSelectedLegendsCallback }: Props) => {
  const defaultSelectedLegendItems = useMemo(() => getDefaultSelectedSeriesItems(deviceId, rthData), [deviceId, rthData])
  const [selectedLegends, setSelectedLegends] = useState({ ...defaultSelectedLegendItems })
  const [plotlyRevision, setPlotlyRevision] = useState(1) // needed to handle multiple zoom in events
  const [timeAtCursor, setTimeAtCursor] = useState<number | undefined>(undefined)
  const [isHovering, setIsHovering] = useState<boolean>(false)
  const [left, setLeft] = useState<number>(0)
  const [graphElement, setGraphElement] = useState<HTMLElement | undefined>(undefined)
  const [showGraphFilterModal, setShowGraphFilterModal] = useState(true)
  const [zones, setZones] = useState(null)
  const [alertsWithInRange, setAlertsWithInRange] = useState(null)

  useEffect(() => {
    updateSelectedLegendsCallback(selectedLegends)
  }, [updateSelectedLegendsCallback, selectedLegends])

  const onLegendDataUpdated = useCallback((legendData, alerts) => {
    setZones(legendData.zones)
    setAlertsWithInRange(alerts)
  }, [])

  const onRelayout = useCallback(({ autosize }) => {
    if (!autosize) {
      zoomedCallback(true)
      setPlotlyRevision(plotlyRevision + 1)
    }
  }, [plotlyRevision, zoomedCallback])

  const onHover = (cursor, left) => {
    setIsHovering(true)
    setTimeAtCursor(cursor)
    setLeft(left)
  }

  const onUnhover = (e: Readonly<Plotly.PlotMouseEvent>) => {
    e.event.preventDefault()
    if (e.event.type !== 'mouseout') { return }

    setIsHovering(false)
    setTimeAtCursor(undefined)
  }

  const onSaveGraphFilter = useCallback((selectedLegendItems) => {
    setSelectedLegends(selectedLegendItems)
    updateSelectedLegendsCallback(selectedLegends)
  }, [updateSelectedLegendsCallback, selectedLegends])

  const onUpdateSelections = useCallback(() => {
    setShowGraphFilterModal(true)
    GA4.sendEvent({ category: 'Customer Detail Page', action: 'Click Update Selections' })
  }, [])

  if (!isRTHDataLoaded) {
    return <Spinner variant="page" />
  }

  return (
    <S.Container>
      <RthPlot
        {...{
          deviceId,
          chartDivId,
          startTime,
          endTime,
          timeZone,
          isZoomed,
          rthData,
          selectedLegends,
          plotlyRevision
        }}
        variant="fullscreen"
        onHoverCallback={onHover}
        onUnhoverCallback={onUnhover}
        onRelayoutCallback={onRelayout}
        updateGraphElementCallback={(element) => setGraphElement(element)}
        onLegendDataCallback={onLegendDataUpdated}
      />
      {isHovering && timeAtCursor && (
        <>
          <S.ToolTipCrossHair left={left} width={2} height={chartHeight(graphElement) - 30} />
          <HoverBox
            selectedLegends={selectedLegends}
            timeAtCursor={timeAtCursor}
            rthData={rthData}
            showHoverData={selectedLegends.showHoverData}
          />
        </>
      )}

      <S.Selection>
        <Button decoration="positive" onPress={onUpdateSelections}text="Update Selections" />
      </S.Selection>

      {showGraphFilterModal && zones && alertsWithInRange &&
        <GraphFilterModal
          rthData={rthData}
          deviceId={deviceId}
          zones={zones}
          alerts={alertsWithInRange}
          closeCallback={() => { setShowGraphFilterModal(false) }}
          onSaveGraphFilterCallback={onSaveGraphFilter}
          showHoverData={selectedLegends.showHoverData}
        />
      }

    </S.Container>
  )
}

export default FullScreenPlot

const S = {
  Selection: styled.div`
    display: flex;
    justify-content: center;
  `,
  NoData: styled(NoData)`
    min-height: 500px;

    @media ${screenSize.small} {
      min-height: 0;
      padding: 3.3em;
    }
  `,
  Container: styled.div`
    position: relative;
  `,
  Plot: styled(PlotlyPlot)`
    width: 100%;
    height: 740px;
  `,
  ToolTipCrossHair: styled.div.withConfig({
    shouldForwardProp: (prop) => !['height', 'width', 'left'].includes(prop)
  })<{ height: number, width: number, left: number }>`
    background-color: ${color('rthLegendBorderHover')};
    height: ${({ height }) => (`${height}px`)};
    width: ${({ width }) => (`${width}px`)};
    pointer-events: none;
    position: absolute;
    top: 0;
    left: ${({ left, width }) => (`${left - (width * 0.5)}px`)};
    z-index: 1;
  `
}
